import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { StepTitle } from "@/components/register/Fields";
import { money } from "@/lib/api";

const PlayerCard = ({ p, i, group, price, onEdit, onRemove, canRemove }) => (
  <div data-testid={`review-player-${i}`} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 flex items-start justify-between gap-4">
    <div>
      <p className="font-display font-bold text-xl">{p.first_name} {p.last_name}</p>
      <p className="mt-1 text-sm text-slate-400">Born {p.date_of_birth} · {p.gender === "male" ? "Boy" : "Girl"} · T-shirt {p.tshirt_size}{p.position && ` · ${p.position}`}</p>
      <p className="mt-3 text-sm">Group: <span data-testid={`review-player-${i}-group`} className="accent-text font-semibold">{group?.name}</span></p>
      <p className="mt-1 text-xs text-slate-500">Social media: {p.consent.photo_social ? "Yes" : "No"} · Advertising: {p.consent.photo_ads ? "Yes" : "No"}</p>
    </div>
    <div className="text-right shrink-0">
      <p className="font-mono-epl">{money(price)}</p>
      <div className="mt-3 flex gap-1 justify-end">
        <button data-testid={`review-edit-${i}`} onClick={onEdit} className="p-2 rounded-lg hover:bg-white/10" aria-label="Edit"><Pencil size={16} /></button>
        {canRemove && <button data-testid={`review-remove-${i}`} onClick={onRemove} className="p-2 rounded-lg hover:bg-white/10 text-red-300" aria-label="Remove"><Trash2 size={16} /></button>}
      </div>
    </div>
  </div>
);

export const StepReview = ({ players, parent, groupFor, price, onEdit, onRemove, onAdd, canAdd, resumed, campName, perPlayer }) => {
  const total = price * players.length;
  return (
    <div data-testid="step-review">
      <StepTitle eyebrow="Step 6" title="Review & pay" sub={`${campName}. Check everything before payment — places are held while you complete payment.${perPlayer ? " With more than one player, you'll pay for each player separately, one after another." : ""}`} />
      {resumed && (
        <p data-testid="review-resume-note" className="mb-4 rounded-2xl border border-blue-400/30 bg-blue-400/[0.06] px-5 py-3 text-sm text-blue-200">
          Welcome back. For privacy, medical details aren't saved on this device — tap edit on each player to re-enter them if needed.
        </p>
      )}
      <div className="space-y-3">
        {players.map((p, i) => (
          <PlayerCard key={i} p={p} i={i} group={groupFor(p)} price={price} onEdit={() => onEdit(i)} onRemove={() => onRemove(i)} canRemove={players.length > 1} />
        ))}
      </div>
      {canAdd && (
        <button data-testid="add-another-player" onClick={onAdd} className="mt-4 w-full h-14 rounded-2xl border border-dashed border-white/20 text-slate-200 hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors flex items-center justify-center gap-2">
          <Plus size={18} /> Add another player (sibling)
        </button>
      )}
      <div className="mt-6 rounded-2xl border border-white/10 p-5 text-sm text-slate-400">
        <p><span className="text-slate-200">{parent.full_name}</span> · {parent.email} · {parent.mobile}</p>
        <p className="mt-1">Emergency: {parent.emergency_name} ({parent.emergency_relationship}) · {parent.emergency_phone}</p>
      </div>
      <div data-testid="review-terms-summary" className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-sm text-slate-400 space-y-2">
        <p className="text-slate-200 font-medium">Camp terms summary</p>
        <p>Cancellations before 25 November 2026 receive a 50% refund; after that, registrations are non-refundable.</p>
        <p>Each group has its own session time within 8:00am–2:00pm — times are emailed by 25 November 2026.</p>
        <p>Players must bring their own water bottle and wear black shorts and black socks.</p>
        <p>Full details: <Link to="/terms" target="_blank" className="accent-text underline">Terms</Link> · <Link to="/refund-policy" target="_blank" className="accent-text underline">Refund policy</Link> · <Link to="/child-safety" target="_blank" className="accent-text underline">Child safety</Link></p>
      </div>
      <div className="mt-6 flex items-end justify-between border-t border-white/10 pt-6">
        <div>
          <p className="text-[11px] font-mono-epl uppercase tracking-wider text-slate-500">Total · {players.length} player{players.length > 1 ? "s" : ""}</p>
          <p className="text-xs text-slate-500 mt-1">Includes GST of {money(Math.round(total / 11))}</p>
          <p data-testid="review-promo-note" className="text-xs text-slate-400 mt-1">Have a promo code? Enter it on the secure payment page.</p>
        </div>
        <p data-testid="review-total" className="font-display font-extrabold text-4xl tracking-tight">{money(total)}</p>
      </div>
    </div>
  );
};
